import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import {
  LayoutDashboard, Users, Calendar, Briefcase, Menu, X, LogOut,
  ClipboardList, Settings, UsersRound, Receipt, Route, Wrench,
  TrendingUp, DollarSign, Wallet, MoreHorizontal, MapPin, Sprout, Sun, Building2, MessageSquare, Zap, Snowflake, HelpCircle } from 'lucide-react';
import { api } from '../services/api';
import RoleSwitcher from './RoleSwitcher';
import ThemeSwitch from './ThemeSwitch';
import { useAuth } from '../hooks/useAuth';
import PracticeBanner from './PracticeBanner';
import ReadOnlyBanner from './ReadOnlyBanner';
import ConnectionBanner from './ConnectionBanner';

// `hint` exists for the three pages that overlap in people's heads — Routes,
// Calendar and Jobs all show "the work". One line each says which is which.
type NavItem = { name: string; href: string; icon: React.ElementType; hint?: string };
type NavGroup = { label: string; items: NavItem[]; collapsible?: boolean };

// Grouped so the sidebar reads as a day, not a 15-item pile.
const NAV: NavGroup[] = [
  { label: 'Every day', items: [
    // One page for the day (owner, Nov 2 2026: "combine field and my day, put needs attention on
    // field and my day, manager check added"): Needs Attention (with Approves), Field, My Day, Manager check.
    { name: 'Today',           href: '/today',     icon: Sun,       hint: 'Needs Attention, your field stops, My Day and the manager check — one page, each part folds' },
    { name: 'Dashboard',       href: '/dashboard', icon: LayoutDashboard },
    { name: 'Routes',          href: '/routes',    icon: Route,     hint: 'The recurring rounds and their stops' },
    { name: 'Client map',      href: '/map',       icon: MapPin,    hint: 'Every yard pinned — tap one to see its route' },
    { name: 'Calendar',        href: '/calendar',  icon: Calendar,  hint: 'Every job laid out by day' },
    { name: 'Jobs',            href: '/jobs',      icon: Briefcase, hint: 'Search, edit and one-off work' },
  ]},
  { label: 'People', items: [
    { name: 'Customers', href: '/customers', icon: Users },
    // Every text on the business number, read and answered here (owner, Oct 14 2026).
    { name: 'Texts',     href: '/texts',     icon: MessageSquare, hint: 'Every text on the business number — read and reply here' },
    { name: 'Employees', href: '/employees', icon: UsersRound },
    // Pay lives with the people it pays (owner, Nov 2 2026).
    { name: 'Crew Pay',  href: '/pay',       icon: Wallet },
  ]},
  { label: 'Money', items: [
    // Owner's words: a QUOTE is the in-person visit (it is on the calendar);
    // a BID is the written line-item proposal (it is a document, not an event).
    // The URLs are older than those words and look crossed, but they are right:
    // /bids renders BidderHome, whose page is titled "Quotes", and /quotes
    // renders Quotes.tsx, titled "Bids". Don't swap them.
    { name: 'Quotes',    href: '/bids',     icon: MapPin,        hint: 'In-person visits to learn what a customer needs; on-site intake' },
    { name: 'Bids',      href: '/quotes',   icon: ClipboardList, hint: 'Written line-item proposals — build, send, sign' },
    { name: 'Billing',   href: '/billing',  icon: Receipt },
    { name: 'Profit',    href: '/profit',   icon: TrendingUp },
  ]},
  // Business accounts: AP billing, sites, contracts, service reports, COI / W-9 (owner, Sep 26 2026).
  { label: 'Commercial', items: [
    { name: 'Today',     href: '/commercial/today', icon: Building2, hint: 'The commercial side: this week at their sites, what needs a hand, bids out, what they owe' },
    { name: 'Accounts',  href: '/commercial', icon: Building2, hint: 'Business accounts: billing, sites, reports and papers' },
  ]},
  { label: 'Setup', items: [
    { name: 'Settings',  href: '/settings',  icon: Settings },
    // Everything the app does by itself, read only (owner, Oct 20 2026: "a list of all my automations just so I know").
    { name: 'Automations', href: '/automations', icon: Zap, hint: 'Everything the app does by itself, and where each switch is' },
  ]},
  // Mowing V1 first: the pages used every day stay on the menu; the rest fold
  // away here (owner, Sep 24 2026 — "you decide"). Opens by itself on one of them.
  { label: 'More', collapsible: true, items: [
    { name: 'Winter work', href: '/winter', icon: Snowflake, hint: 'One-time work customers chose to have done Dec 1 – Mar 1 at 10% off: set the dates, text them' },
    // What customers keep asking, as a sheet and as FAQs; every upsell the crew sent in (owner, Nov 1 2026).
    { name: 'FAQs',      href: '/faq',       icon: HelpCircle,    hint: 'Questions customers asked, the FAQs built from them, and every upsell' },
    // Programs, mowing add-ons and this week's lawn-care work on one page (owner, Nov 2 2026).
    { name: 'Lawn care', href: '/lawn-care', icon: Sprout,      hint: 'Treatment programs, the add-ons that ride a mow, and what is coming this week' },
    { name: 'Cash Flow', href: '/cashflow', icon: DollarSign },
    { name: 'Equipment', href: '/equipment', icon: Wrench },
  ]},
];
const ALL_ITEMS = NAV.flatMap(g => g.items);

// Thumb-reach shortcuts on phones; "More" opens the full menu.
const MOBILE_TABS: NavItem[] = [
  { name: 'Today',     href: '/today',     icon: Sun },
  { name: 'Routes',    href: '/routes',    icon: Route },
  { name: 'Calendar',  href: '/calendar',  icon: Calendar },
  { name: 'Customers', href: '/customers', icon: Users },
  { name: 'Pay',       href: '/pay',       icon: Wallet },
];
// The owner's thumb bar: Today carries his field stops too; Pay stays in the menu.
const FIELD_TABS: NavItem[] = MOBILE_TABS.filter(t => t.href !== '/pay');
/** A login that holds field hats too (the owner) gets the Field page. */
const hasField = (user: any) => !!user?.choices?.field?.some((f: any) => f.home === '/field');

function isActive(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(href + '/');
}

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  // Pages that live off the menu still get a real title in the top bar.
  const OFF_MENU: Record<string, string> = { '/projects': 'Projects', '/admin/profit': 'Profit' };
  const current = ALL_ITEMS.find(i => isActive(location.pathname, i.href))
    || Object.entries(OFF_MENU).map(([href, name]) => ({ href, name })).find(i => isActive(location.pathname, i.href));
  const tabs = hasField(user) ? FIELD_TABS : MOBILE_TABS;
  // Switching hats from the drawer lands on another page: the drawer gets out of the way.
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);
  const today = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  // Texts nobody has opened yet: a count on the Texts row, and a dot on "More" where the row is hidden on a phone.
  const { data: unreadData } = useQuery<{ unread: number }>('texts-unread', () => api.get('/messages/unread-count').then(r => r.data),
    { refetchInterval: 30000, retry: false, staleTime: 10000 });
  const unread = Number(unreadData?.unread) || 0;

  return (
    <div className="min-h-screen bg-canvas">
      <PracticeBanner />
      <ReadOnlyBanner />
      <ConnectionBanner />
      {/* Mobile drawer */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-[2px]" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 flex w-72 flex-col min-h-0 bg-brand-600 shadow-brand-lg rounded-r-3xl dark:bg-plum dark:shadow-lift">
            <SidebarContent pathname={location.pathname} user={user} logout={logout} unread={unread} onClose={() => setSidebarOpen(false)} />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-col flex-grow min-h-0 h-full bg-brand-600 shadow-brand dark:bg-plum dark:shadow-none dark:border-r dark:border-snow/10">
          <SidebarContent pathname={location.pathname} user={user} logout={logout} unread={unread} />
        </div>
      </div>

      {/* Main */}
      <div className="lg:pl-64">
        <div className="sticky top-0 z-40 flex h-16 items-center gap-3 border-b border-gray-200 bg-white/95 backdrop-blur px-4 sm:px-6 shadow-soft">
          <button className="press -ml-1 h-11 w-11 flex items-center justify-center rounded-2xl text-gray-900 hover:bg-brand-50 lg:hidden" onClick={() => setSidebarOpen(true)} aria-label="Menu">
            <Menu className="h-6 w-6" />
          </button>
          <span className="lg:hidden flex-shrink-0 h-10 w-10 rounded-2xl bg-brand-600 shadow-brand flex items-center justify-center">
            <img src="/logo-white.png" alt="" width={28} height={28} className="h-7 w-7 object-contain" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-[15px] font-black uppercase tracking-tight text-gray-900 truncate">{current?.name || 'Keep Austin Mowed'}</p>
          </div>
          <span className="hidden sm:inline text-sm font-semibold text-gray-600">{today}</span>
          {/* Field ↔ Admin, where it can be seen on a computer and a phone alike (owner, Sep 19 2026). */}
          <RoleSwitcher compact />
          <div className="h-9 w-9 rounded-full bg-brand-600 text-black shadow-brand flex items-center justify-center text-sm font-black" title={user?.name}>
            {user?.name?.charAt(0).toUpperCase()}
          </div>
        </div>
        <main className="py-5 sm:py-6 pb-24 lg:pb-8">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom tabs */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 shadow-[0_-6px_20px_-8px_rgba(74,0,84,0.18)] safe-area-inset-bottom">
        <div className="flex">
          {tabs.map(t => {
            const active = isActive(location.pathname, t.href);
            return (
              <Link key={t.href} to={t.href}
                className={`press flex-1 min-w-0 flex flex-col items-center gap-1 pt-2 pb-1.5 text-[11px] tracking-tight ${active ? 'text-black font-bold dark:text-snow' : 'text-gray-600 font-medium'}`}>
                <span className={`px-4 py-1 rounded-full ${active ? 'bg-brand-600 shadow-brand text-black' : ''}`}><t.icon className="h-5 w-5" /></span>
                {t.name}
              </Link>
            );
          })}
          <button onClick={() => setSidebarOpen(true)} className="press flex-1 flex flex-col items-center gap-1 pt-2 pb-1.5 text-[11px] font-semibold text-gray-600">
            <span className="relative px-4 py-1">
              <MoreHorizontal className="h-5 w-5" />
              {unread > 0 && <span className="absolute top-0 right-2 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" aria-label={`${unread} unread texts`} />}
            </span>
            More
          </button>
        </div>
      </nav>
    </div>
  );
}

function SidebarContent({ pathname, user, logout, unread = 0, onClose }: { pathname: string; user: any; logout: () => void; unread?: number; onClose?: () => void }) {
  const [moreOpen, setMoreOpen] = React.useState<boolean>(() => {
    try { return localStorage.getItem('nav_more_open') === '1'; } catch { return false; }
  });
  const toggleMore = () => setMoreOpen(v => {
    try { localStorage.setItem('nav_more_open', v ? '0' : '1'); } catch { /* private window */ }
    return !v;
  });
  return (
    <>
      <div className="flex h-20 items-center justify-between px-4">
        <Link to="/dashboard" onClick={onClose} className="press flex items-center gap-3 min-w-0">
          <span className="flex-shrink-0 h-11 w-11 rounded-2xl bg-white/20 flex items-center justify-center dark:bg-brand-600 dark:shadow-brand">
            <img src="/logo-white.png" alt="" width={30} height={30} className="h-[30px] w-[30px] object-contain" />
          </span>
          <div className="min-w-0">
            <div className="text-[14px] font-black uppercase tracking-tight text-black leading-none whitespace-nowrap dark:text-snow">Keep Austin Mowed</div>
            <div className="text-xs font-bold text-black/75 leading-tight mt-1 dark:text-snow/70">Admin</div>
          </div>
        </Link>
        {onClose && (
          <button onClick={onClose} aria-label="Close menu" className="press h-10 w-10 flex items-center justify-center rounded-2xl text-black hover:bg-white/25 lg:hidden dark:text-snow dark:hover:bg-snow/10">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 min-h-0 px-3 pb-4 overflow-y-auto overscroll-contain">
        {NAV.map(group => (
          <div key={group.label} className="mt-3 first:mt-1">
            {group.collapsible ? (
              <button type="button" onClick={toggleMore} aria-expanded={moreOpen || group.items.some(i => isActive(pathname, i.href))}
                className="w-full text-left px-3 mb-1.5 text-[11px] font-black uppercase tracking-widest text-black/80 dark:text-brand-700">
                {group.label} {moreOpen || group.items.some(i => isActive(pathname, i.href)) ? '▾' : '▸'}
              </button>
            ) : (
              <p className="px-3 mb-1.5 text-[11px] font-black uppercase tracking-widest text-black/80 dark:text-brand-700">{group.label}</p>
            )}
            {(!group.collapsible || moreOpen || group.items.some(i => isActive(pathname, i.href))) && (
            <div className="space-y-1">
              {group.items.filter(i => i.href !== '/field' || hasField(user)).map(item => {
                const active = isActive(pathname, item.href);
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={onClose}
                    className={`press group flex items-center gap-3 px-3 py-2.5 text-[14px] rounded-2xl ${
                      active ? 'bg-white text-black font-black shadow-soft dark:bg-brand-600 dark:shadow-brand' : 'text-black font-semibold hover:bg-white/25 dark:text-gray-800 dark:hover:bg-snow/10'
                    }`}
                  >
                    <item.icon className={`h-[18px] w-[18px] flex-shrink-0 ${active ? 'text-brand-600 dark:text-black' : 'text-black/80 dark:text-gray-600'}`} />
                    <span className="min-w-0">
                      {item.name}
                      {item.href === '/texts' && unread > 0 && (
                        <span className="ml-2 inline-flex min-w-[1.25rem] h-5 px-1.5 items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-black align-middle">{unread}</span>
                      )}
                      {/* The one-line "which is which" only shows on the page you are on,
                          where it can be read; on every row it was grey noise. */}
                      {item.hint && active && (
                        <span className="block text-[11px] font-medium text-gray-600 leading-tight truncate dark:text-black/70">{item.hint}</span>
                      )}
                    </span>
                  </Link>
                );
              })}
            </div>
            )}
          </div>
        ))}
      </nav>

      <div className="p-3 space-y-2">
        {/* Light / Auto / Dark (owner, Sep 21 2026). Auto follows the phone. */}
        <ThemeSwitch tone="menu" />
        <div className="flex items-center gap-3 px-2.5 py-2.5 rounded-2xl bg-white/20 dark:bg-snow/5">
          <div className="h-10 w-10 rounded-full bg-white shadow-soft flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-black text-brand-700">{user?.name?.charAt(0).toUpperCase()}</span>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-black text-black truncate dark:text-snow">{user?.name}</p>
            <p className="text-xs font-semibold text-black/75 truncate dark:text-snow/70">{user?.role === 'viewer' ? 'View only' : user?.role === 'manager' ? 'Manager' : 'Admin'}</p>
            <div className="mt-1"><RoleSwitcher up /></div>
          </div>
          <button onClick={logout} aria-label="Sign out" className="press h-10 w-10 flex items-center justify-center rounded-2xl text-black hover:bg-white/30 dark:text-snow dark:hover:bg-snow/10" title="Sign out">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
      </div>
    </>
  );
}
